class HospitalHandover extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    this.dataset.initialized = 'true';

    const root = this;
    const DEPTS = ['ICU','ICU ISO','ISO','RESPIRATORY','GASTRO','CHEMO','WARDS - DOG','WARDS - CAT'];
    const SEVS = ['Stable','Watcher','Unstable'];
    const SEV_ORDER = {'Unstable':1,'Watcher':2,'Stable':3};
    const SYNTHS = ['Pending','Completed'];
    const CODES = ['CPR','DNR','Unsure'];
    const LEVELS = ['Level 1','Level 2','Level 3'];

    const PE_DEF = 'VITALS:\nHR = \nRR = \nMM = \nCRT = \nPulse Quality = \nTemp = \nBW/BCS = \nMentation: \nBP (sys) = Not taken\nSPO2 = Not taken\n\nCVS: No murmur No arrhythmia\nRespiratory: Normal sounds and effort\nHydration: Euhydrated\nAlimentary/abdomen: Soft\nRenal/urinary/urogenital: Normal appearance and on palpation\nNeurological: Appropriate\nMSK: No lameness, pain, atrophy or swelling\nEars: Bilateral Clear\nEyes: OU Responsive\nNose: Non-Obstructive\nOral: Dental Grade 1\nDermatologic: Normal skin and coat\nLymph Nodes: Normal\nRectal: Not performed';
    const PAST24_DEF = 'Eating: Unsure\nDrinking: Unsure\nDefecating: Unsure\nUrinating: Unsure\nV/R: Unsure\nFeeding Tube Rate: -\nNGT Suction: -\nBW Change: -\nUOP: -\nBG Trend: -';
    const DX_DEF = 'Blood Tests:\n\nFAST SCAN:\nAFAST:\n\nTFAST:\nRHS: \nLHS: \nLa:Ao: ';

    const FULL_FIELDS = [
      {k:'signalment', label:'Signalment', type:'input', ph:'e.g. 5y 2m MN DSH'},
      {k:'admitted', label:'Date admitted', type:'input', ph:'e.g. May 27 7:00 PM'},
      {k:'billing', label:'Billing', def:'12 hours hospital'},
      {k:'updates', label:'Updates', ph:'What changed this shift'},
      {k:'rdvm', label:'RDVM notes', def:'RDVM: \nEmailed: No'},
      {k:'warnings', label:'Warnings', def:'None'},
      {k:'reasons', label:'Reason(s) for admission', ph:'Presenting reason(s)'},
      {k:'history', label:'History', tall:true},
      {k:'pe', label:'Physical exam', def:PE_DEF, tall:true},
      {k:'past24', label:'Past 12 to 24 hours', def:PAST24_DEF, tall:true},
      {k:'diagnostics', label:'Diagnostics', def:DX_DEF, tall:true},
      {k:'procedures', label:'Procedures'},
      {k:'assessment', label:'Assessment / problem list', tall:true},
      {k:'medications', label:'Treatments: medications', tall:true, ph:'Drug, dose, route, frequency'},
      {k:'othercare', label:'Treatments: other care and monitoring'},
      {k:'plan', label:'Plan', def:'REFERRAL: None', tall:true},
      {k:'comms', label:'Client communications'},
      {k:'finances', label:'Finances', def:'Current Estimate: \nCurrent Deposit: \nTime Period: '},
      {k:'labs', label:'Lab tests pending', def:'VetCT: \nVDL: '}
    ];

    const SHORT_FIELDS = [
      {k:'signalment', label:'Signalment', type:'input', ph:'e.g. 5y 2m MN DSH'},
      {k:'admitted', label:'Date admitted', type:'input', ph:'e.g. May 27 7:00 PM'},
      {k:'billing', label:'Billing today', def:''},
      {k:'warnings', label:'Warnings', def:'None'},
      {k:'reasons', label:'Assessment / reasons'},
      {k:'history', label:'History', tall:true},
      {k:'updates', label:'Updates this shift', tall:true},
      {k:'plan', label:'Plan', def:'REFERRAL: None', tall:true},
      {k:'finances', label:'Finances', def:'Current Estimate: \nCurrent Deposit: \nTime Period: '},
      {k:'labs', label:'Lab tests pending', def:'VetCT: \nVDL: '}
    ];

    const IPASS_FIELDS = [
      {k:'ip_summary', label:'P - Patient summary', tall:true, ph:'Events leading to admission, hospital course so far, ongoing assessment, plan'},
      {k:'ip_actions', label:'A - Action list', tall:true, ph:'To-do items for the next vet, each with a time and an owner'},
      {k:'ip_contingency', label:'S - Situation awareness and contingency planning', tall:true, ph:'What is happening now, what may happen next: if X happens, do Y'}
    ];
    const RECEIVER_FIELD = {k:'ip_receiver', label:'Receiver questions / read-back notes', ph:'Filled by the receiving vet during synthesis'};

    const $ = (sel) => root.querySelector(sel);
    const $$ = (sel) => Array.from(root.querySelectorAll(sel));
    const storageKey = root.dataset.storageKey || 'aav-handover-v1';
    let uidc = 0;
    let saveTimer = null;
    let state = {shift:{hospital:'',date:'',shiftType:'',from:'',to:'',notes:''},patients:[],pts:[],dis:[]};

    const esc = (s) => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    const uid = () => 'p' + Date.now().toString(36) + (++uidc);
    const fieldsFor = (tpl) => tpl === 'short' ? SHORT_FIELDS : FULL_FIELDS;

    function defaultsFor(tpl) {
      const f = {};
      fieldsFor(tpl).forEach((d) => { f[d.k] = d.def || ''; });
      IPASS_FIELDS.forEach((d) => { f[d.k] = ''; });
      f[RECEIVER_FIELD.k] = '';
      return f;
    }

    function newPatient(name, dept, tpl) {
      return {
        id:uid(), name, dept, tpl,
        level:'Level 2', code:'CPR', sev:'Stable', synth:'Pending',
        f:defaultsFor(tpl)
      };
    }

    function deptPatients(dept) {
      return state.patients
        .filter((p) => p.dept === dept)
        .slice()
        .sort((a,b) => (SEV_ORDER[a.sev] || 9) - (SEV_ORDER[b.sev] || 9));
    }

    function scheduleSave() {
      clearTimeout(saveTimer);
      saveTimer = setTimeout(saveLocal, 400);
    }

    function saveLocal() {
      try {
        localStorage.setItem(storageKey, JSON.stringify({v:1,app:'aav-handover',state}));
      } catch (e) {}
    }

    function adoptPatientObj(p) {
      if (!p || typeof p.name !== 'string') return null;
      const tpl = p.tpl === 'short' ? 'short' : 'full';
      const np = newPatient(p.name, DEPTS.includes(p.dept) ? p.dept : DEPTS[0], tpl);
      if (LEVELS.includes(p.level)) np.level = p.level;
      if (CODES.includes(p.code)) np.code = p.code;
      if (SEVS.includes(p.sev)) np.sev = p.sev;
      else if (p.sev === 'Critical') np.sev = 'Unstable';
      if (SYNTHS.includes(p.synth)) np.synth = p.synth;
      else np.synth = p.synth === 'Yes' ? 'Completed' : 'Pending';
      if (p.f && typeof p.f === 'object') {
        Object.keys(np.f).forEach((k) => {
          if (typeof p.f[k] === 'string') np.f[k] = p.f[k];
        });
      }
      return np;
    }

    function adoptState(s) {
      state = {shift:{hospital:'',date:'',shiftType:'',from:'',to:'',notes:''},patients:[],pts:[],dis:[]};
      if (s && s.shift) {
        Object.keys(state.shift).forEach((k) => {
          if (typeof s.shift[k] === 'string') state.shift[k] = s.shift[k];
        });
      }
      (Array.isArray(s?.patients) ? s.patients : []).forEach((p) => {
        const np = adoptPatientObj(p);
        if (np) state.patients.push(np);
      });
      ['pts','dis'].forEach((listKey) => {
        (Array.isArray(s?.[listKey]) ? s[listKey] : []).forEach((r) => {
          if (!r || typeof r !== 'object') return;
          const row = {name:String(r.name || ''),time:String(r.time || ''),note:String(r.note || '')};
          if (r.snap && typeof r.snap === 'object') row.snap = r.snap;
          state[listKey].push(row);
        });
      });
    }

    function loadLocal() {
      try {
        const raw = localStorage.getItem(storageKey);
        if (!raw) return false;
        const obj = JSON.parse(raw);
        if (!obj || obj.app !== 'aav-handover' || !obj.state) return false;
        adoptState(obj.state);
        return true;
      } catch (e) {
        return false;
      }
    }

    function notice(msg) {
      const n = $('[data-notice]');
      if (!n) return;
      n.innerHTML = msg;
      if (msg) {
        clearTimeout(notice._t);
        notice._t = setTimeout(() => { n.innerHTML = ''; }, 6000);
      }
    }

    function bindShift() {
      $$('[data-shift]').forEach((el) => {
        const key = el.dataset.shift;
        el.value = state.shift[key] || '';
        el.addEventListener('input', () => {
          state.shift[key] = el.value;
          scheduleSave();
        });
      });
    }

    function refreshShiftForm() {
      $$('[data-shift]').forEach((el) => {
        el.value = state.shift[el.dataset.shift] || '';
      });
    }

    function sevChipClass(sev) {
      return sev === 'Stable' ? 'ok' : (sev === 'Watcher' ? 'warn' : 'bad');
    }

    function hasWarning(p) {
      const w = (p.f.warnings || '').trim();
      return w !== '' && w.toLowerCase() !== 'none';
    }

    function makeBtn(txt, cls, fn) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = cls;
      b.textContent = txt;
      b.addEventListener('click', fn);
      return b;
    }

    function makeSel(p, key, label, opts, onchange) {
      const l = document.createElement('label');
      l.className = 'f';
      l.textContent = label;
      const s = document.createElement('select');
      opts.forEach((o) => {
        const val = Array.isArray(o) ? o[0] : o;
        const txt = Array.isArray(o) ? o[1] : o;
        const op = document.createElement('option');
        op.value = val;
        op.textContent = txt;
        s.appendChild(op);
      });
      s.value = p[key];
      s.addEventListener('change', () => {
        p[key] = s.value;
        scheduleSave();
        if (onchange) onchange();
      });
      l.appendChild(s);
      return l;
    }

    function autoGrow(el) {
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight + 2, 600) + 'px';
    }

    function makeField(p, def) {
      const l = document.createElement('label');
      l.className = 'f';
      l.textContent = def.label;
      let el;
      if (def.type === 'input') {
        el = document.createElement('input');
        el.type = 'text';
      } else {
        el = document.createElement('textarea');
        if (def.tall) el.className = 'tall';
        el.rows = def.tall ? 5 : 2;
      }
      if (def.ph) el.placeholder = def.ph;
      el.value = p.f[def.k] || '';
      el.addEventListener('input', () => {
        p.f[def.k] = el.value;
        scheduleSave();
        if (def.k === 'warnings' || def.k === 'signalment') updateHead(p);
        if (el.tagName === 'TEXTAREA') autoGrow(el);
      });
      if (el.tagName === 'TEXTAREA') requestAnimationFrame(() => autoGrow(el));
      l.appendChild(el);
      return l;
    }

    function updateHead(p) {
      const d = root.querySelector(`details.pcard[data-id="${CSS.escape(p.id)}"]`);
      if (!d) return;
      const sum = d.querySelector('summary .headinfo');
      let chips = '';
      chips += `<span class="chip ${sevChipClass(p.sev)}">${esc(p.sev)}</span>`;
      if (p.code === 'DNR') chips += '<span class="chip bad">DNR</span>';
      else if (p.code === 'Unsure') chips += '<span class="chip warn">Code unsure</span>';
      else chips += '<span class="chip ok">CPR</span>';
      if (hasWarning(p)) chips += '<span class="chip bad">Warning</span>';
      chips += `<span class="chip plain">${p.tpl === 'short' ? 'Short' : 'Full'}</span>`;
      sum.innerHTML = `<span class="arrow">▶</span><span class="pname">${esc(p.name || '(unnamed)')}</span><span class="psig">${esc(p.f.signalment || '')}</span><span class="spacer"></span>${chips}`;
    }

    function updateAllHeads() {
      state.patients.forEach(updateHead);
    }

    function removePatient(id) {
      state.patients = state.patients.filter((p) => p.id !== id);
      scheduleSave();
      renderBoard();
    }

    function rebuildTpl(p, dEl) {
      const stack = dEl.querySelector('[data-role="fields"]');
      const kept = p.f;
      p.f = defaultsFor(p.tpl);
      Object.keys(p.f).forEach((k) => {
        if (typeof kept[k] === 'string' && kept[k] !== '') p.f[k] = kept[k];
      });
      stack.innerHTML = '';
      fieldsFor(p.tpl).forEach((def) => stack.appendChild(makeField(p, def)));
      scheduleSave();
      updateHead(p);
    }

    function moveToList(p, listKey) {
      const t = new Date();
      const hh = String(t.getHours()).padStart(2,'0') + ':' + String(t.getMinutes()).padStart(2,'0');
      state[listKey].push({name:p.name,time:hh,note:(p.f.signalment || ''),snap:JSON.parse(JSON.stringify(p))});
      removePatient(p.id);
      renderLists();
      notice(`${listKey === 'dis' ? 'Moved to Discharges' : 'Moved to PTS / died'}: <b>${esc(p.name || '(unnamed)')}</b>. Use Restore if this was a mistake.`);
    }

    function restoreFromList(listKey, i) {
      const row = state[listKey][i];
      if (!row || !row.snap) return;
      const np = adoptPatientObj(row.snap);
      if (!np) {
        notice('Could not restore that patient.');
        return;
      }
      state[listKey].splice(i,1);
      state.patients.push(np);
      scheduleSave();
      renderBoard();
      renderLists();
      notice(`Restored to the board: <b>${esc(np.name || '(unnamed)')}</b>`);
    }

    function buildCard(p, open) {
      const d = document.createElement('details');
      d.className = 'pcard';
      d.dataset.id = p.id;
      if (open) d.open = true;

      const sum = document.createElement('summary');
      sum.className = 'cardhead';

      const info = document.createElement('span');
      info.className = 'headinfo';
      sum.appendChild(info);

      const delBtn = document.createElement('button');
      delBtn.type = 'button';
      delBtn.className = 'btn small danger headdel';
      delBtn.textContent = 'Delete';
      delBtn.setAttribute('aria-label','Delete this patient from the handover');
      delBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (delBtn.dataset.armed) {
          notice(`Deleted from the handover: <b>${esc(p.name || '(unnamed)')}</b>`);
          removePatient(p.id);
          return;
        }
        delBtn.dataset.armed = '1';
        delBtn.textContent = 'Confirm delete';
        delBtn.classList.add('armed');
        setTimeout(() => {
          delete delBtn.dataset.armed;
          delBtn.textContent = 'Delete';
          delBtn.classList.remove('armed');
        }, 3000);
      });
      sum.appendChild(delBtn);
      d.appendChild(sum);

      const body = document.createElement('div');
      body.className = 'cardbody';

      const g1 = document.createElement('div');
      g1.className = 'grid cols4';
      g1.appendChild(makeSel(p,'dept','Department',DEPTS,renderBoard));
      g1.appendChild(makeSel(p,'tpl','Template',[['full','Full case notes'],['short','Short notes']],() => rebuildTpl(p,d)));
      g1.appendChild(makeSel(p,'level','Level',LEVELS));
      g1.appendChild(makeSel(p,'code','Code status',CODES,() => updateHead(p)));
      body.appendChild(g1);

      const gn = document.createElement('div');
      gn.className = 'grid cols3';
      const nl = document.createElement('label');
      nl.className = 'f';
      nl.textContent = 'Patient name';
      const ni = document.createElement('input');
      ni.type = 'text';
      ni.value = p.name;
      ni.addEventListener('input', () => {
        p.name = ni.value;
        updateHead(p);
        scheduleSave();
      });
      nl.appendChild(ni);
      gn.appendChild(nl);
      body.appendChild(gn);

      const stack = document.createElement('div');
      stack.className = 'fieldstack';
      stack.dataset.role = 'fields';
      fieldsFor(p.tpl).forEach((def) => stack.appendChild(makeField(p, def)));
      body.appendChild(stack);

      const box = document.createElement('div');
      box.className = 'ipassbox';
      box.innerHTML = '<h3>I-PASS handover</h3>';
      const gi = document.createElement('div');
      gi.className = 'grid cols3';
      gi.appendChild(makeSel(p,'sev','I - Illness severity',SEVS,() => {
        updateHead(p);
        renderBoard();
      }));
      box.appendChild(gi);

      const si = document.createElement('div');
      si.className = 'fieldstack';
      si.style.marginTop = '10px';
      IPASS_FIELDS.forEach((def) => si.appendChild(makeField(p, def)));
      box.appendChild(si);

      const gs = document.createElement('div');
      gs.className = 'grid cols3';
      gs.style.marginTop = '10px';
      gs.appendChild(makeSel(p,'synth','S - Synthesis by receiver',SYNTHS));
      box.appendChild(gs);

      const sr = document.createElement('div');
      sr.className = 'fieldstack';
      sr.style.marginTop = '10px';
      sr.appendChild(makeField(p, RECEIVER_FIELD));
      box.appendChild(sr);
      body.appendChild(box);

      const act = document.createElement('div');
      act.className = 'cardactions';
      act.appendChild(makeBtn('Print / PDF this patient','btn small primary',() => printOne(p.id)));
      act.appendChild(makeBtn('Copy this patient as text','btn small',() => copyText(p.id)));
      act.appendChild(makeBtn('Mark discharged','btn small',() => moveToList(p,'dis')));
      act.appendChild(makeBtn('Mark PTS / died','btn small',() => moveToList(p,'pts')));

      const rm = makeBtn('Remove','btn small danger',() => {
        if (rm.dataset.armed) {
          removePatient(p.id);
          return;
        }
        rm.dataset.armed = '1';
        rm.textContent = 'Confirm remove';
        rm.classList.add('armed');
        setTimeout(() => {
          delete rm.dataset.armed;
          rm.textContent = 'Remove';
          rm.classList.remove('armed');
        },3000);
      });
      act.appendChild(rm);
      body.appendChild(act);

      d.appendChild(body);
      return d;
    }

    function renderBoard() {
      const board = $('[data-board]');
      const openSet = {};
      board.querySelectorAll('details.pcard').forEach((d) => {
        if (d.open) openSet[d.dataset.id] = true;
      });
      board.innerHTML = '';
      DEPTS.forEach((dept) => {
        const pats = deptPatients(dept);
        const sec = document.createElement('section');
        sec.className = 'deptsec';
        const head = document.createElement('div');
        head.className = 'depthead';
        head.innerHTML = `<h2>${esc(dept)}</h2><span class="count">${pats.length}</span><span class="rule"></span>`;
        sec.appendChild(head);
        if (!pats.length) {
          const em = document.createElement('p');
          em.className = 'deptempty';
          em.textContent = 'No patients';
          sec.appendChild(em);
        }
        pats.forEach((p) => sec.appendChild(buildCard(p, openSet[p.id])));
        board.appendChild(sec);
      });
      updateAllHeads();
    }

    function listInput(row,key,ph) {
      const i = document.createElement('input');
      i.type = 'text';
      i.placeholder = ph;
      i.value = row[key] || '';
      i.addEventListener('input', () => {
        row[key] = i.value;
        scheduleSave();
      });
      return i;
    }

    function renderLists() {
      [['pts','[data-pts-list]'],['dis','[data-dis-list]']].forEach(([listKey,sel]) => {
        const host = $(sel);
        host.innerHTML = '';
        state[listKey].forEach((row,i) => {
          const r = document.createElement('div');
          r.className = 'listrow';
          r.appendChild(listInput(row,'name','Patient name'));
          r.appendChild(listInput(row,'time','Time'));
          r.appendChild(listInput(row,'note','Notes / signalment / outcome'));

          const btns = document.createElement('span');
          btns.style.display = 'flex';
          btns.style.gap = '6px';

          if (row.snap) btns.appendChild(makeBtn('Restore','btn small',() => restoreFromList(listKey,i)));
          btns.appendChild(makeBtn('Remove','btn small danger',() => {
            state[listKey].splice(i,1);
            scheduleSave();
            renderLists();
          }));

          r.appendChild(btns);
          host.appendChild(r);
        });
      });
    }

    function shiftMetaHtml() {
      const s = state.shift;
      const cell = (k,v) => `<div><span class="k">${k}</span>${esc(v || '-')}</div>`;
      return `<div class="pv-meta">${cell('Hospital',s.hospital)}${cell('Date',s.date)}${cell('Shift',s.shiftType)}${cell('Handover from',s.from)}${cell('Handover to',s.to)}${cell('Notes',s.notes)}</div>`;
    }

    function censusHtml() {
      let rows = '';
      DEPTS.forEach((dept) => {
        deptPatients(dept).forEach((p) => {
          rows += `<tr><td>${esc(dept)}</td><td><b>${esc(p.name || '(unnamed)')}</b></td><td>${esc(p.f.signalment || '')}</td><td>${esc(p.sev)}</td><td>${esc(p.code)}</td><td>${esc(hasWarning(p) ? (p.f.warnings || '') : '-')}</td></tr>`;
        });
      });
      if (!rows) rows = '<tr><td colspan="6">No hospitalised patients</td></tr>';
      return `<table class="pv-census"><thead><tr><th>Dept</th><th>Patient</th><th>Signalment</th><th>Severity</th><th>Code</th><th>Warnings</th></tr></thead><tbody>${rows}</tbody></table>`;
    }

    function simpleListHtml(title,rows) {
      if (!rows.length) return '';
      let h = `<div class="pv-h2">${title}</div><table class="pv-census"><tbody>`;
      rows.forEach((r) => {
        h += `<tr><td style="width:28%"><b>${esc(r.name || '-')}</b></td><td style="width:12%">${esc(r.time || '')}</td><td>${esc(r.note || '')}</td></tr>`;
      });
      return h + '</tbody></table>';
    }

    function patientHtml(p,first,single) {
      let h = `<section class="pv-patient${first ? ' first' : ''}" data-id="${esc(p.id)}">`;
      if (single) {
        const s = state.shift;
        h += `<div class="pv-singlemeta">Hospital handover${s.hospital ? ' - ' + esc(s.hospital) : ''}${s.date ? ' - ' + esc(s.date) : ''}${s.from ? ' - from ' + esc(s.from) : ''}${s.to ? ' to ' + esc(s.to) : ''}</div>`;
      }

      h += `<div class="pv-band"><div class="pv-dept">${esc(p.dept)}</div><div class="pv-name">${esc(p.name || '(unnamed)')}</div><div class="pv-sub">${esc(p.f.signalment || '')}${p.f.admitted ? ' · Admitted: ' + esc(p.f.admitted) : ''}</div><div class="pv-chips"><span class="pv-chip sev-${p.sev.toLowerCase()}">${esc(p.sev)}</span><span class="pv-chip${p.code === 'DNR' ? ' code-dnr' : ''}">Code: ${esc(p.code)}</span><span class="pv-chip">${esc(p.level)}</span>${hasWarning(p) ? '<span class="pv-chip warnflag">Warning</span>' : ''}</div></div>`;

      fieldsFor(p.tpl).forEach((def) => {
        if (def.k === 'signalment' || def.k === 'admitted') return;
        const v = (p.f[def.k] || '').trim();
        if (!v) return;
        h += `<div class="pv-field"><div class="k">${esc(def.label)}</div><div class="v">${esc(p.f[def.k])}</div></div>`;
      });

      h += '<div class="pv-ipass"><div class="pv-h3">I-PASS handover</div>';
      h += `<div class="pv-field"><div class="k">I - Illness severity</div><div class="v">${esc(p.sev)}</div></div>`;
      IPASS_FIELDS.forEach((def) => {
        const v = (p.f[def.k] || '').trim();
        h += `<div class="pv-field"><div class="k">${esc(def.label)}</div><div class="v">${v ? esc(p.f[def.k]) : '-'}</div></div>`;
      });
      h += `<div class="pv-field"><div class="k">S - Synthesis by receiver</div><div class="v">${esc(p.synth)}</div></div>`;
      const rn = (p.f[RECEIVER_FIELD.k] || '').trim();
      if (rn) h += `<div class="pv-field"><div class="k">${esc(RECEIVER_FIELD.label)}</div><div class="v">${esc(p.f[RECEIVER_FIELD.k])}</div></div>`;
      h += '</div></section>';
      return h;
    }

    function buildPrintView(onlyId) {
      const pv = $('[data-printview]');
      pv.dataset.manual = '1';
      let h = '';
      if (!onlyId) {
        h += `<div class="pv-cover"><div class="pv-title">HOSPITAL HANDOVER (I-PASS)</div><p class="pv-tag">I-PASS: Illness severity, Patient summary, Action list, Situation awareness and contingency planning, Synthesis by receiver</p>${shiftMetaHtml()}<div class="pv-h2">Hospital census (sickest first within each department)</div>${censusHtml()}${simpleListHtml('PTS / died on shift',state.pts)}${simpleListHtml('Discharges on shift',state.dis)}</div>`;
        DEPTS.forEach((dept) => deptPatients(dept).forEach((p) => { h += patientHtml(p,false,false); }));
      } else {
        const p = state.patients.find((x) => x.id === onlyId);
        if (p) h += patientHtml(p,true,true);
      }
      pv.innerHTML = h;
    }

    function printAll() {
      buildPrintView(null);
      window.print();
    }

    function printOne(id) {
      buildPrintView(id);
      window.print();
    }

    function lineBlock(label,v) {
      v = (v || '').trim();
      if (!v) return '';
      return `${label.toUpperCase()}:\n${v}\n\n`;
    }

    function patientText(p) {
      let t = '========================================\n';
      t += `${(p.name || '(unnamed)').toUpperCase()}  [${p.dept}]\n`;
      if (p.f.signalment) t += p.f.signalment + '\n';
      t += `Severity: ${p.sev} | Code: ${p.code} | ${p.level} | Template: ${p.tpl === 'short' ? 'Short notes' : 'Full case notes'}\n\n`;
      fieldsFor(p.tpl).forEach((def) => {
        if (def.k === 'signalment') return;
        t += lineBlock(def.label,p.f[def.k]);
      });
      t += `I-PASS\n------\nI - Illness severity: ${p.sev}\n\n`;
      IPASS_FIELDS.forEach((def) => { t += lineBlock(def.label,p.f[def.k]); });
      t += `S - Synthesis by receiver: ${p.synth}\n`;
      t += lineBlock(RECEIVER_FIELD.label,p.f[RECEIVER_FIELD.k]);
      return t;
    }

    function handoverText(onlyId) {
      if (onlyId) {
        const p = state.patients.find((x) => x.id === onlyId);
        return p ? patientText(p) : '';
      }
      const s = state.shift;
      let t = 'HOSPITAL HANDOVER (I-PASS)\n';
      t += [s.hospital,s.date,s.shiftType ? s.shiftType + ' shift' : '',s.from ? 'From: ' + s.from : '',s.to ? 'To: ' + s.to : ''].filter(Boolean).join(' | ') + '\n';
      if (s.notes) t += 'Notes: ' + s.notes + '\n';
      t += '\n';
      DEPTS.forEach((dept) => deptPatients(dept).forEach((p) => { t += patientText(p) + '\n'; }));
      if (state.pts.length) {
        t += 'PTS / DIED ON SHIFT:\n';
        state.pts.forEach((r) => { t += '- ' + [r.name,r.time,r.note].filter(Boolean).join(' | ') + '\n'; });
        t += '\n';
      }
      if (state.dis.length) {
        t += 'DISCHARGES ON SHIFT:\n';
        state.dis.forEach((r) => { t += '- ' + [r.name,r.time,r.note].filter(Boolean).join(' | ') + '\n'; });
        t += '\n';
      }
      return t;
    }

    function legacyCopy(txt,done) {
      const ta = document.createElement('textarea');
      ta.value = txt;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      let ok = false;
      try { ok = document.execCommand('copy'); } catch (e) {}
      document.body.removeChild(ta);
      done(ok);
    }

    function copyText(onlyId) {
      const txt = handoverText(onlyId);
      const done = (ok) => notice(ok ? 'Copied to clipboard — paste into an email or your PMS.' : 'Could not copy automatically. Use Print / Save PDF instead.');
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(txt).then(() => done(true),() => legacyCopy(txt,done));
      } else {
        legacyCopy(txt,done);
      }
    }

    function saveFile() {
      saveLocal();
      const data = JSON.stringify({v:1,app:'aav-handover',savedAt:new Date().toISOString(),state},null,1);
      const d = state.shift.date || new Date().toISOString().slice(0,10);
      const fname = `handover-${d}.json`;
      try {
        const blob = new Blob([data],{type:'application/json'});
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href),4000);
        notice('Handover file saved.');
      } catch (e) {
        notice('Could not create the file. Your work is still auto-saved in this browser.');
      }
    }

    function loadFile(file) {
      const r = new FileReader();
      r.onload = () => {
        try {
          const obj = JSON.parse(r.result);
          if (!obj || obj.app !== 'aav-handover' || !obj.state) {
            notice('That file is not a saved handover.');
            return;
          }
          adoptState(obj.state);
          refreshShiftForm();
          renderBoard();
          renderLists();
          saveLocal();
          notice(`Handover loaded: <b>${state.patients.length}</b> patient(s).`);
        } catch (e) {
          notice('Could not read that file.');
        }
      };
      r.readAsText(file);
    }

    function resetClearButton(button) {
      delete button.dataset.armed;
      button.textContent = button.dataset.defaultText || 'Clear shift';
      button.classList.remove('armed');
    }

    function boot() {
      const deptSel = $('[data-new-dept]');
      DEPTS.forEach((d) => {
        const o = document.createElement('option');
        o.value = d;
        o.textContent = d;
        deptSel.appendChild(o);
      });
      if (DEPTS.includes(root.dataset.defaultDepartment)) deptSel.value = root.dataset.defaultDepartment;

      const restored = loadLocal();
      bindShift();
      renderBoard();
      renderLists();

      $('[data-action="print-all"]').addEventListener('click',printAll);
      $('[data-action="copy-all"]').addEventListener('click',() => copyText(null));
      $('[data-action="save-file"]').addEventListener('click',saveFile);
      $('[data-action="load-file"]').addEventListener('click',() => $('[data-load-file-input]').click());
      $('[data-load-file-input]').addEventListener('change',(e) => {
        if (e.target.files?.[0]) loadFile(e.target.files[0]);
        e.target.value = '';
      });

      const clearBtn = $('[data-action="clear-shift"]');
      clearBtn.dataset.defaultText = clearBtn.textContent;
      clearBtn.addEventListener('click',() => {
        if (clearBtn.dataset.armed) {
          resetClearButton(clearBtn);
          state = {shift:{hospital:'',date:'',shiftType:'',from:'',to:'',notes:''},patients:[],pts:[],dis:[]};
          try { localStorage.removeItem(storageKey); } catch (e) {}
          refreshShiftForm();
          renderBoard();
          renderLists();
          notice('Shift cleared.');
          return;
        }
        clearBtn.dataset.armed = '1';
        clearBtn.textContent = 'Really clear everything?';
        clearBtn.classList.add('armed');
        setTimeout(() => resetClearButton(clearBtn),3500);
      });

      $('[data-action="add-patient"]').addEventListener('click',() => {
        const nameEl = $('[data-new-name]');
        const name = nameEl.value.trim();
        const dept = $('[data-new-dept]').value;
        const tpl = $('[data-new-template]').value;
        if (!name) {
          notice('Give the patient a name first.');
          nameEl.focus();
          return;
        }
        const p = newPatient(name,dept,tpl);
        state.patients.push(p);
        nameEl.value = '';
        scheduleSave();
        renderBoard();
        const d = root.querySelector(`details.pcard[data-id="${CSS.escape(p.id)}"]`);
        if (d) {
          d.open = true;
          d.scrollIntoView({behavior:'smooth',block:'start'});
        }
        $('[data-srmsg]').textContent = `Added ${name} to ${dept}`;
      });

      $('[data-new-name]').addEventListener('keydown',(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          $('[data-action="add-patient"]').click();
        }
      });

      $('[data-action="add-pts"]').addEventListener('click',() => {
        state.pts.push({name:'',time:'',note:''});
        scheduleSave();
        renderLists();
      });

      $('[data-action="add-discharge"]').addEventListener('click',() => {
        state.dis.push({name:'',time:'',note:''});
        scheduleSave();
        renderLists();
      });

      window.addEventListener('beforeprint',() => {
        const pv = $('[data-printview]');
        if (!pv.dataset.manual) buildPrintView(null);
      });
      window.addEventListener('afterprint',() => {
        delete $('[data-printview]').dataset.manual;
      });

      if (restored && (state.patients.length || state.shift.hospital || state.pts.length || state.dis.length)) {
        notice(`Draft restored from this browser — <b>${state.patients.length}</b> patient(s). Use Save file to move it between computers.`);
      }
    }

    boot();
  }
}

if (!customElements.get('s-hospital-handover')) {
  customElements.define('s-hospital-handover', HospitalHandover);
}
